import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Settings as SettingsIcon, Plus, GripVertical, Edit2, Trash2, Save, X, Zap } from 'lucide-react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { token, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('statuses');
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStatus, setEditingStatus] = useState(null);
  const [newStatusName, setNewStatusName] = useState('');
  const [showAddStatus, setShowAddStatus] = useState(false);
  const [statusActions, setStatusActions] = useState({});
  const [editingAction, setEditingAction] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [visibility, setVisibility] = useState({ airports: [], finance: [], business_dev: [] });
  const [savingVisibility, setSavingVisibility] = useState(false);

  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (token && isAdmin) {
      fetchStatuses();
      fetchStatusActions();
      fetchUsersAndVisibility();
    }
  }, [token, isAdmin]);

  const fetchUsersAndVisibility = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, visRes] = await Promise.all([
        axios.get(`${API}/users`, { headers, params: { rows_per_page: 1000 } }),
        axios.get(`${API}/settings/user-visibility`, { headers }),
      ]);
      setAllUsers(usersRes.data?.records || usersRes.data || []);
      setVisibility({
        airports: visRes.data?.airports || [],
        finance: visRes.data?.finance || [],
        business_dev: visRes.data?.business_dev || [],
      });
    } catch (error) {
      console.error('Failed to load users/visibility:', error);
    }
  };

  const toggleVisibilityUser = (section, userId) => {
    setVisibility((prev) => {
      const list = prev[section] || [];
      return { ...prev, [section]: list.includes(userId) ? list.filter((id) => id !== userId) : [...list, userId] };
    });
  };

  const handleSaveVisibility = async () => {
    setSavingVisibility(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/settings/user-visibility`, visibility, { headers });
    } catch (error) {
      alert('Failed to save user visibility: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingVisibility(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/reference-data/project-status`, { headers });
      // Extract records from response
      const statusList = response.data?.records || response.data || [];
      // Sort by order field if it exists, otherwise by name
      const sorted = statusList.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.field_84_raw || '').localeCompare(b.field_84_raw || '');
      });
      setStatuses(sorted);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusActions = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/settings/status-actions`, { headers });
      setStatusActions(response.data || {});
    } catch (error) {
      console.error('Failed to fetch status actions:', error);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order field for all items
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    setStatuses(updatedItems);

    // Save new order to backend
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/settings/status-order`, {
        statuses: updatedItems.map((s, idx) => ({ id: s.id, order: idx }))
      }, { headers });
    } catch (error) {
      console.error('Failed to save status order:', error);
      fetchStatuses(); // Revert on error
    }
  };

  const handleAddStatus = async () => {
    if (!newStatusName.trim()) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/reference-data/project-status`, {
        field_84: newStatusName,
        order: statuses.length
      }, { headers });
      
      setNewStatusName('');
      setShowAddStatus(false);
      fetchStatuses();
    } catch (error) {
      console.error('Failed to add status:', error);
      alert('Failed to add status');
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/reference-data/project-status/${status.id}`, {
        field_84: status.field_84_raw
      }, { headers });
      
      setEditingStatus(null);
      fetchStatuses();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm('Are you sure? This will affect all projects with this status.')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/reference-data/project-status/${statusId}`, { headers });
      fetchStatuses();
    } catch (error) {
      console.error('Failed to delete status:', error);
      alert('Failed to delete status');
    }
  };

  const handleSaveAction = async (fromStatus, toStatus, action) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/settings/status-actions`, {
        from_status: fromStatus,
        to_status: toStatus,
        action_type: action.type,
        action_config: action.config
      }, { headers });
      
      setEditingAction(null);
      fetchStatusActions();
    } catch (error) {
      console.error('Failed to save action:', error);
      alert('Failed to save action');
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <SettingsIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Admin Access Required</h3>
          <p className="text-gray-500">You need administrator privileges to access settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <SettingsIcon className="w-7 h-7" />
            Settings
          </h1>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex gap-4 px-6">
            <button
              onClick={() => setActiveTab('statuses')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition ${
                activeTab === 'statuses'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Project Statuses
            </button>
            <button
              onClick={() => setActiveTab('actions')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition ${
                activeTab === 'actions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Status Actions
            </button>
            <button
              onClick={() => setActiveTab('visibility')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition ${
                activeTab === 'visibility'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="settings-tab-visibility"
            >
              User Visibility
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'statuses' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Manage Project Statuses</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Drag to reorder. Order affects display in Kanban and filters.
                  </p>
                </div>
                <button
                  onClick={() => setShowAddStatus(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <Plus className="w-4 h-4" />
                  Add Status
                </button>
              </div>

              {showAddStatus && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newStatusName}
                      onChange={(e) => setNewStatusName(e.target.value)}
                      placeholder="Enter status name..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
                      autoFocus
                    />
                    <button
                      onClick={handleAddStatus}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setShowAddStatus(false);
                        setNewStatusName('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {statuses.length > 0 ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="statuses" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-2"
                      >
                        {statuses.map((status, index) => (
                        <Draggable
                          key={status.id}
                          draggableId={status.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center gap-3 p-4 bg-white border rounded-lg transition ${
                                snapshot.isDragging
                                  ? 'border-blue-500 shadow-lg'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing" title="Drag to reorder">
                                <GripVertical className="w-5 h-5 text-gray-400" />
                              </div>

                              {editingStatus?.id === status.id ? (
                                <input
                                  type="text"
                                  value={editingStatus.field_84_raw}
                                  onChange={(e) =>
                                    setEditingStatus({
                                      ...editingStatus,
                                      field_84_raw: e.target.value
                                    })
                                  }
                                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleUpdateStatus(editingStatus);
                                    if (e.key === 'Escape') setEditingStatus(null);
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <span className="flex-1 font-medium text-gray-900">
                                  {status.field_84_raw}
                                </span>
                              )}

                              <div className="flex items-center gap-2">
                                {editingStatus?.id === status.id ? (
                                  <>
                                    <button
                                      onClick={() => handleUpdateStatus(editingStatus)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                    >
                                      <Save className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => setEditingStatus(null)}
                                      className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setEditingStatus(status)}
                                      className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteStatus(status.id)}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <p className="text-gray-500 text-center py-8">No statuses found</p>
              )}
            </div>
          )}

          {activeTab === 'actions' && (
            <div>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Status Change Actions
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Configure what happens when a project moves between statuses.
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Coming Soon:</strong> Define automated actions like sending notifications,
                    creating tasks, or updating fields when projects change status.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-medium text-gray-900 mb-4">Example Actions:</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-green-500 mt-0.5" />
                      <span>When status changes from "Lead" → "Ongoing": Create task "Project Kickoff"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-blue-500 mt-0.5" />
                      <span>When status changes to "Won": Send email notification to team</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-purple-500 mt-0.5" />
                      <span>When status changes to "Lost": Archive related documents</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'visibility' && (
            <div data-testid="settings-visibility-panel">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Section Filter Visibility</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Choose which users appear in each division's owner filter. Leave a section empty to show all users.
                  </p>
                </div>
                <button
                  onClick={handleSaveVisibility}
                  disabled={savingVisibility}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="save-visibility-btn"
                >
                  <Save className="w-4 h-4" />
                  {savingVisibility ? 'Saving...' : 'Save'}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { key: 'airports', label: 'Airports Division' },
                  { key: 'finance', label: 'Finance' },
                  { key: 'business_dev', label: 'Business Development' },
                ].map((section) => (
                  <div key={section.key} className="border border-gray-200 rounded-lg" data-testid={`visibility-section-${section.key}`}>
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                      <h3 className="font-medium text-gray-900 text-sm">{section.label}</h3>
                      <span className="text-xs text-gray-500">
                        {(visibility[section.key] || []).length === 0 ? 'All users' : `${visibility[section.key].length} selected`}
                      </span>
                    </div>
                    <div className="max-h-80 overflow-y-auto p-2 space-y-1">
                      {allUsers.map((u) => {
                        const uid = u.id;
                        const uname = u.field_12 || u.field_12_raw?.full || u.field_13_raw?.email || uid;
                        const checked = (visibility[section.key] || []).includes(uid);
                        return (
                          <label key={uid} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer" data-testid={`visibility-${section.key}-user-${uid}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleVisibilityUser(section.key, uid)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 truncate">{uname}</span>
                          </label>
                        );
                      })}
                      {allUsers.length === 0 && <p className="text-xs text-gray-400 p-2">No users found</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
